import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useGrowthDispositionSources } from "@/hooks/useGrowthChannelsServerPaging";
import type { GrowthDispositionRow } from "@/services/growth-channels-page.service";
import { cn } from "@/lib/utils";
import { CalendarCheck, CalendarX, Download, FileText, Loader2, Phone, PhoneOff, TrendingUp, Users, XCircle } from "lucide-react";

const fmtCurrency = (value: number) => {
  if (!Number.isFinite(value) || value <= 0) return "$0";
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 10_000) return `$${Math.round(value / 1000)}k`;
  if (value >= 1000) return `$${(value / 1000).toFixed(1)}k`;
  return `$${Math.round(value).toLocaleString()}`;
};
const fmtPct = (value: number) => `${Math.round(Number.isFinite(value) ? value : 0)}%`;

const DEFAULT_FROM = "2026-01-01";
const DEFAULT_TO = "2026-03-31";

function downloadCSV(filename: string, headers: string[], rows: Array<Array<string | number>>) {
  const csv = [headers.join(","), ...rows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function GrowthDispositionServer() {
  const [sourceType, setSourceType] = useState("all");
  const [dateFrom, setDateFrom] = useState(DEFAULT_FROM);
  const [dateTo, setDateTo] = useState(DEFAULT_TO);
  const { data, loading, error } = useGrowthDispositionSources({ enabled: true, sourceType, dateFrom, dateTo });

  const sourceTypes = useMemo(
    () => Array.from(new Set(data.map((row) => row.sourceType).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [data],
  );

  const totals = useMemo(() => data.reduce((acc, row) => ({
    totalLeads: acc.totalLeads + row.totalLeads,
    contacted: acc.contacted + row.contacted,
    noContact: acc.noContact + row.noContact,
    appointmentsSet: acc.appointmentsSet + row.appointmentsSet,
    appointmentsCompleted: acc.appointmentsCompleted + row.appointmentsCompleted,
    noShow: acc.noShow + row.noShow,
    sold: acc.sold + row.sold,
    rejected: acc.rejected + row.rejected,
    revenue: acc.revenue + row.revenue,
    cost: acc.cost + row.cost,
    issueCount: acc.issueCount + row.issueCount,
    creditAmount: acc.creditAmount + row.creditAmount,
  }), {
    totalLeads: 0,
    contacted: 0,
    noContact: 0,
    appointmentsSet: 0,
    appointmentsCompleted: 0,
    noShow: 0,
    sold: 0,
    rejected: 0,
    revenue: 0,
    cost: 0,
    issueCount: 0,
    creditAmount: 0,
  }), [data]);

  const reasonTotals = useMemo(() => {
    const totals = new Map<string, number>();
    for (const row of data) {
      for (const [reason, count] of Object.entries(row.rejectionReasons ?? {})) {
        totals.set(reason, (totals.get(reason) ?? 0) + Number(count || 0));
      }
    }
    return Array.from(totals.entries()).sort((a, b) => b[1] - a[1]);
  }, [data]);

  const conversionRate = totals.appointmentsCompleted > 0 ? (totals.sold / totals.appointmentsCompleted) * 100 : 0;
  const contactRate = totals.totalLeads > 0 ? (totals.contacted / totals.totalLeads) * 100 : 0;

  const handleExport = () => {
    downloadCSV(`growth-dispositions-${dateFrom}-to-${dateTo}.csv`, [
      "Source", "Type", "Total Leads", "Contacted", "No Contact", "Appts Set", "Appts Completed", "No Show", "Sold", "Rejected", "Revenue", "Cost", "Conversion %",
    ], data.map((row) => [
      row.name,
      row.sourceType,
      row.totalLeads,
      row.contacted,
      row.noContact,
      row.appointmentsSet,
      row.appointmentsCompleted,
      row.noShow,
      row.sold,
      row.rejected,
      row.revenue,
      row.cost,
      row.conversionRate,
    ]));
  };

  return (
    <div className="space-y-5" data-growth-server-section="dispositions">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-semibold">Dispositions</p>
          <h3 className="text-sm font-semibold text-foreground">Lead outcomes by source</h3>
          <p className="text-[11px] text-muted-foreground mt-0.5">Server-backed source accountability from {dateFrom} to {dateTo}</p>
        </div>
        <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={handleExport} disabled={data.length === 0}>
          <Download className="h-3.5 w-3.5" /> Export CSV
        </Button>
      </div>

      <MetricStrip
        totals={totals}
        contactRate={contactRate}
        conversionRate={conversionRate}
        loading={loading}
      />

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.12em]">From</Label>
          <Input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} className="h-8 text-xs w-[150px]" />
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.12em]">To</Label>
          <Input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} className="h-8 text-xs w-[150px]" />
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.12em]">Source Type</Label>
          <Select value={sourceType} onValueChange={setSourceType}>
            <SelectTrigger className="h-8 text-xs w-[160px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sources</SelectItem>
              {sourceTypes.map((type) => <SelectItem key={type} value={type} className="text-xs capitalize">{type.replace(/_/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {loading && <span className="inline-flex items-center gap-2 text-[12px] text-muted-foreground pb-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading</span>}
        {error && <span className="text-[12px] text-warning pb-2">Disposition view failed to load.</span>}
      </div>

      {reasonTotals.length > 0 && (
        <div className="rounded-sm border border-border/70 bg-muted/15 p-3">
          <p className="text-[10px] uppercase tracking-[0.16em] font-semibold text-muted-foreground mb-2">Top rejection reasons</p>
          <div className="flex flex-wrap gap-2">
            {reasonTotals.slice(0, 8).map(([reason, count]) => (
              <Badge key={reason} variant="outline" className="bg-background text-[11px]">
                {reason}: {count}
              </Badge>
            ))}
          </div>
        </div>
      )}

      <div className="border border-border/70 rounded-sm overflow-hidden">
        <div className="grid grid-cols-[minmax(0,1.35fr)_110px_82px_82px_82px_82px_82px_90px_90px] items-center gap-3 px-4 py-2.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground border-b border-border bg-muted/20">
          <span>Source</span>
          <span>Type</span>
          <span className="text-right">Leads</span>
          <span className="text-right">Contacted</span>
          <span className="text-right">Appts</span>
          <span className="text-right">No-show</span>
          <span className="text-right">Sold</span>
          <span className="text-right">Conv</span>
          <span className="text-right">Revenue</span>
        </div>
        <ul className="divide-y divide-border">
          {data.length === 0 && <li className="px-4 py-8 text-center text-[13px] text-muted-foreground">{loading ? "Loading disposition rows..." : "No source dispositions match the filters."}</li>}
          {data.map((row) => <DispositionRow key={`${row.sourceType}:${row.name}`} row={row} />)}
        </ul>
      </div>
    </div>
  );
}

function MetricStrip({
  totals,
  contactRate,
  conversionRate,
  loading,
}: {
  totals: {
    totalLeads: number;
    contacted: number;
    noContact: number;
    appointmentsSet: number;
    appointmentsCompleted: number;
    noShow: number;
    sold: number;
    rejected: number;
    revenue: number;
    cost: number;
    issueCount: number;
    creditAmount: number;
  };
  contactRate: number;
  conversionRate: number;
  loading: boolean;
}) {
  const metrics = [
    { label: "Total leads", value: String(totals.totalLeads), sub: loading ? "refreshing" : "selected range", icon: Users },
    { label: "Contacted", value: String(totals.contacted), sub: `${fmtPct(contactRate)} contact rate`, icon: Phone },
    { label: "No contact", value: String(totals.noContact), sub: "unreached leads", icon: PhoneOff },
    { label: "Appointments", value: String(totals.appointmentsSet), sub: `${totals.appointmentsCompleted} completed`, icon: CalendarCheck },
    { label: "No-show", value: String(totals.noShow), sub: "missed demos", icon: CalendarX },
    { label: "Sold", value: String(totals.sold), sub: `${fmtPct(conversionRate)} conv`, icon: TrendingUp },
    { label: "Rejected", value: String(totals.rejected), sub: `${totals.issueCount} issues`, icon: XCircle },
    { label: "Revenue", value: fmtCurrency(totals.revenue), sub: `${fmtCurrency(totals.cost)} cost`, icon: FileText },
  ];

  return (
    <dl className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-px overflow-hidden rounded-sm border border-border/60 bg-border/60">
      {metrics.map((metric) => {
        const Icon = metric.icon;
        return (
          <div key={metric.label} className="bg-background px-3 py-3">
            <dt className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              <Icon className="h-3 w-3" />
              {metric.label}
            </dt>
            <dd className="mt-1 text-lg font-semibold tracking-tight text-foreground tabular-nums">{metric.value}</dd>
            <p className="mt-0.5 text-[11px] text-muted-foreground">{metric.sub}</p>
          </div>
        );
      })}
    </dl>
  );
}

function DispositionRow({ row }: { row: GrowthDispositionRow }) {
  return (
    <li className="grid grid-cols-[minmax(0,1.35fr)_110px_82px_82px_82px_82px_82px_90px_90px] items-center gap-3 px-4 py-3 text-[13px] hover:bg-muted/30 transition-colors">
      <div className="min-w-0">
        <div className="font-semibold text-foreground truncate">{row.name}</div>
        <div className="text-[11px] text-muted-foreground truncate">{row.rejected} rejected · {fmtCurrency(row.creditAmount)} credits</div>
      </div>
      <span className="capitalize text-muted-foreground truncate">{row.sourceType.replace(/_/g, " ")}</span>
      <span className="text-right tabular-nums text-foreground">{row.totalLeads}</span>
      <span className="text-right tabular-nums text-foreground">{row.contacted}</span>
      <span className="text-right tabular-nums text-foreground">{row.appointmentsSet}</span>
      <span className={cn("text-right tabular-nums", row.noShow > 0 ? "text-warning" : "text-muted-foreground")}>{row.noShow}</span>
      <span className="text-right tabular-nums text-foreground">{row.sold}</span>
      <span className={cn("text-right tabular-nums font-semibold", row.conversionRate >= 25 ? "text-success" : row.conversionRate < 10 ? "text-destructive" : "text-foreground")}>{fmtPct(row.conversionRate)}</span>
      <span className="text-right tabular-nums text-foreground">{fmtCurrency(row.revenue)}</span>
    </li>
  );
}
